import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs';

import { ClarityService } from '@core/analytics/clarity.service';

/**
 * Mapea operaciones exitosas/fallidas del backend a eventos de Clarity (sitio público).
 *
 * Centraliza los hitos del huésped (registro, login, búsqueda, favoritos, contacto a hotel,
 * cambios de cuenta) en un solo lugar; se cruzan en el panel con los tags de superficie/hotel
 * que setea ClarityTrackingService. Empareja por método + pathname; primera regla que coincide.
 */
interface EventRule {
    method: string;
    test: (pathname: string) => boolean;
    onSuccess?: string;
    onError?: string;
}

const eq = (path: string) => (p: string): boolean => p === path;
const endsWith = (suffix: string) => (p: string): boolean => p === suffix || p.endsWith(suffix);
const has = (fragment: string) => (p: string): boolean => p.includes(fragment);

const RULES: EventRule[] = [
    // ---- Autenticación del huésped ----
    { method: 'POST', test: endsWith('/auth/signin'), onSuccess: 'login_succeeded', onError: 'login_failed' },
    { method: 'POST', test: endsWith('/auth/signup'), onSuccess: 'guest_registered', onError: 'guest_register_failed' },
    { method: 'POST', test: endsWith('/auth/verify-email'), onSuccess: 'guest_email_verified' },
    { method: 'POST', test: endsWith('/auth/resend-verification-code'), onSuccess: 'verification_code_resent' },

    // ---- Descubrimiento / engagement (insights) ----
    { method: 'POST', test: endsWith('/guest/searches'), onSuccess: 'search_performed' },
    { method: 'POST', test: has('/guest/likes/'), onSuccess: 'hotel_favorited' },

    // ---- Contacto con el hotel (chat) ----
    { method: 'POST', test: (p) => p.includes('/chat/conversations/') && p.endsWith('/messages'), onSuccess: 'guest_message_sent' },
    { method: 'POST', test: endsWith('/chat/conversations'), onSuccess: 'guest_conversation_started' },

    // ---- Cuenta ----
    { method: 'PUT', test: endsWith('/account/password'), onSuccess: 'account_password_changed' },
    { method: 'PUT', test: eq('/account'), onSuccess: 'account_profile_updated' },
];

function matchRule(method: string, url: string): EventRule | null {
    let pathname: string;
    try {
        pathname = new URL(url, 'http://x').pathname;
    } catch {
        return null;
    }
    for (const rule of RULES) {
        if (rule.method === method && rule.test(pathname)) return rule;
    }
    return null;
}

export const clarityInterceptor: HttpInterceptorFn = (req, next) => {
    const rule = matchRule(req.method, req.url);
    if (!rule) return next(req);

    const clarity = inject(ClarityService);

    return next(req).pipe(
        tap({
            next: event => {
                if (event instanceof HttpResponse && rule.onSuccess) {
                    clarity.event(rule.onSuccess);
                }
            },
            error: () => {
                if (rule.onError) clarity.event(rule.onError);
            },
        })
    );
};
